/**
 * Platform whiteboard stamps — Phase 13 P2.
 *
 * "Stamps" are the football-domain shorthand for pre-made symbol
 * objects coaches drop on a whiteboard (X, O, square, arrow). The
 * underlying mechanism is generic: any domain that declares the
 * `whiteboard_stamps` capability gets the same picker.
 *
 * Phase 13 P2 graduation
 * ----------------------
 *
 * Before P2: football's bespoke `WhiteboardStamps` lived inside
 * the play designer page and was wired with hard-coded football
 * symbol assets.
 *
 * After P2: the symbol set is read from the active domain pack
 * via `domainPack.assetCatalog.whiteboard_stamps` (an empty array
 * for non-football domains until they author their own symbol
 * library). The tool itself is platform code.
 *
 * The stamps tool binds on `requiresCapability('whiteboard_stamps')`,
 * which the platform whiteboard catalog row declares. Football's
 * existing whiteboard scenes inherit the new capability via
 * migration 0220's capability_snapshot re-sync.
 */
import React from 'react';

import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import { requiresCapability } from '../predicates';

const FALLBACK_STAMPS = [
  { id: 'circle',  symbol: '○', label: 'Offense actor (O)' },
  { id: 'cross',   symbol: '×', label: 'Defense actor (X)' },
  { id: 'square',  symbol: '▢', label: 'OL / Block' },
  { id: 'arrow',   symbol: '→', label: 'Direction arrow' },
];

function WhiteboardStamps() {
  const { eventBus, domainPack } = useDesigner();
  // Domain pack's `assetCatalog.whiteboard_stamps` overrides the
  // platform fallback. This is what makes the tool tenant-aware
  // without any per-domain code.
  const stamps =
    (domainPack && domainPack.assetCatalog && domainPack.assetCatalog.whiteboard_stamps)
    || FALLBACK_STAMPS;

  const pick = (s) => eventBus.emit('whiteboard.stamp_picked', { stamp: s });

  return (
    <div
      className="designer-tool designer-tool--canvas-overlay-toolbar"
      data-tool-id="platform.whiteboard_stamps"
      role="toolbar"
      aria-label="Whiteboard stamps"
    >
      {stamps.map((s) => (
        <button
          type="button"
          key={s.id}
          className="designer-tool__button designer-tool__button--icon"
          aria-label={s.label}
          title={s.label}
          onClick={() => pick(s)}
        >
          {s.symbol}
        </button>
      ))}
    </div>
  );
}

export const whiteboardStamps = new DesignerTool({
  id:         'platform.whiteboard_stamps',
  label:      'Stamps',
  slot:       'CANVAS_OVERLAY',
  category:   'annotation',
  applies_to: requiresCapability('whiteboard_stamps'),
  Component:  WhiteboardStamps,
  weight:     52,
  helpText:   'Drop pre-made symbol stamps on the whiteboard surface.',
});

export default whiteboardStamps;
